import { Injectable, IterableDiffer, IterableDiffers, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, Subscription, forkJoin, from, of } from 'rxjs';
import { filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { ToastrService } from 'ngx-toastr';
import * as _ from 'lodash';

import { MonitorsService } from '../http-services/monitors.service';
import { PlayerService } from '../http-services/player.service';
import { UIService } from '../utils/ui.service';
import { MonitorHub } from '../../../shared-module/hubs/monitor.hub';
import { TagService } from '../http-services/tag.service';
import { AuthService } from '../auth/auth.service';
import { PlaylistsService } from '../http-services/playlists.service';

import { Monitor } from '../../models/entities/monitor.type';
import { BehaviorScreen } from '../../models/view-models/monitor-models/screen.type';
import { PluralAssignment } from '../../models/view-models/monitor-models/monitor-assignment.type';
import { MonitorKey } from '../../models/view-models/monitor-models/monitor-key.type';
import { IMonitorSelectInfo } from '../../interfaces/monitor-selected-info.interface';
import { AppConfig } from '../../../app.settings';
import { Machine } from '../../models/entities/machine.type';
import { PlaylistDTO } from '../../models/view-models/playlist-models/playlist-dto.model';
import { TagViewModel } from './../../models/view-models/tag-models/tag-view.model';
import { MonitorTagPluralAssignmentModel } from '../../models/view-models/monitor-models/monitor-tag-plural-assignment.model';
import { PlaylistDialogMessagesEnum } from '../../enums/toastr-messages/playlist-dialog-messages.enum';
import { ToastrMessageEnum } from '../../enums/toastr-messages/toastr-message.enum';
import { MonitorTagDialogMessagesEnum } from '../../enums/toastr-messages/monitor-tag-dialog-messages.enum';
import { AppConstants } from '../../../app.constants';
import { ScreenContentStatus } from '../../enums/screen-status.enum';
import { MonitorMenuDialogDessagesEnum } from '../../enums/toastr-messages/monitor-menu-dialog-messages.enum';
import { MachinesService } from '../http-services/machines.service';
import { MonitorChangeViewService } from '../http-services/monitor-change-view.service';
import { ScreensState } from '../../models/view-models/monitor-models/screens-state.model';

@Injectable()
export class ScreensStateService {
  _screens: BehaviorScreen[] = [];
  _selectedScreens: BehaviorScreen[] = [];

  selectedScreensChanged = new BehaviorSubject<BehaviorScreen[]>([]);
  screensCollectionChanged = new BehaviorSubject<BehaviorScreen[]>(null);
  someSchedulesUpdated: Subject<void> = new Subject();
  someScreenUpdated: Subject<BehaviorScreen> = new Subject();
  monitorTagChanged: BehaviorSubject<MonitorKey[]> = new BehaviorSubject(null);
  activeMonitorTagsChanged: BehaviorSubject<TagViewModel[]> = new BehaviorSubject([]);
  locationsChanged: BehaviorSubject<Machine[]> = new BehaviorSubject<Machine[]>([]);
  isLocationLoading: BehaviorSubject<boolean> = new BehaviorSubject(true);
  locationChanged: Subject<string> = new Subject<string>();
  monitorsInTheDashboard: BehaviorSubject<BehaviorScreen[]> = new BehaviorSubject<BehaviorScreen[]>([]);
  filteredMonitorsInTheDashboard = new BehaviorSubject<BehaviorScreen[]>([]);
  public locationWithNotRunningService: string[] = [];

  public isSplashEnabled: Subject<boolean> = new Subject();
  public isSimplifiedSplashEnabled: Subject<boolean> = new Subject();
  public isAnyPlaylistRunning: Map<string, boolean> = new Map();
  public playlistStateDataChanged: BehaviorSubject<Map<string, boolean>> = new BehaviorSubject(new Map());
  public pendingScreens = new BehaviorSubject<Map<string, ScreenContentStatus>>(null);
  public playlistSelectionChanged = new BehaviorSubject<PlaylistDTO>(null);

  subscriptions: Subscription = new Subscription();

  private differ: IterableDiffer<BehaviorScreen> | null = null;
  private cachedScreensState: ScreensState | null = null;
  private lastSelectedScreens: BehaviorScreen[] | null = null;

  constructor(
    public monitorsDataService: MonitorsService,
    public monitorHub: MonitorHub,
    public toastrService: ToastrService,
    public ngZone: NgZone,
    public uiService: UIService,
    public config: AppConfig,
    public route: Router,
    public playerService: PlayerService,
    public tagService: TagService,
    private monitorChangeViewService: MonitorChangeViewService,
    private machinesService: MachinesService,
    private authService: AuthService,
    private playlistDataService: PlaylistsService,
    private differs: IterableDiffers
  ) { }

  initMonitors() {
    this.initializeVariables();

    this._reloadActiveMonitors();

    this.subscriptions.add(
      this.monitorHub.ScreenCacheRemoved.subscribe(data => {
        this.removeInactiveMonitors(data);
      })
    );

    this.subscriptions.add(
      this.monitorHub.ScreenCacheAdded.pipe(
        filter(monitor => !!monitor)
      ).subscribe(monitor => {
        this.addMonitors([monitor]);
      })
    );

    this.subscriptions.add(
      this.monitorHub.MachineProfileUpdated.subscribe((data: Machine) => {
        this._reloadLocations([data]);
      })
    );
  }

  selectAll(screenToSelect: BehaviorScreen[] = this.monitorsInTheDashboard.value) {
    screenToSelect.forEach((screen: BehaviorScreen) => {
      if (
        (screen.Machine.ShowPrimaryScreen && screen.isVisible) ||
        (!screen.Machine.ShowPrimaryScreen &&
          !screen.IsPrimary &&
          screen.isVisible)
      ) {
        this.selectScreen(screen, true);
      }
    });
  }

  tagItemChanged(tag: TagViewModel, isActive: boolean): void {
    const currentActiveTags: TagViewModel[] = this.activeMonitorTagsChanged.getValue();
    if (isActive) {
      _.remove(currentActiveTags, (x: TagViewModel) => x.Id === tag.Id);
    } else {
      currentActiveTags.push(tag);
    }

    const assignedMonitorKeys: MonitorKey[] = _.flattenDeep(currentActiveTags.map(x => x.AssignedMonitorKeys));
    let taggedMonitors: MonitorKey[] = [];
    if (assignedMonitorKeys && assignedMonitorKeys.length) {
      taggedMonitors = assignedMonitorKeys;
    } else {
      taggedMonitors = isActive ? null : [];
    }
    this.monitorTagChanged.next(taggedMonitors);
    this.activeMonitorTagsChanged.next(currentActiveTags);
  }

  changeScreensByIds(screenIds: MonitorKey[]) {
    const selectedScreens = _.filter(this._screens, (loc: BehaviorScreen) =>
      _.some(
        screenIds,
        (scr: MonitorKey) =>
          scr.MachinePhysicalAddress === loc.MachinePhysicalAddress &&
          scr.MonitorHandler === loc.MonitorHandler
      )
    );
    this.changeScreens(selectedScreens);
  }

  unselectAll() {
    this._screens.forEach(screen => {
      this.unselectScreen(screen, true);
    });
  }

  selectScreen(screen: BehaviorScreen, multiple: boolean = false) {
    const data = {
      Monitor: screen,
      IsSelected: true,
      Multiselect: multiple
    };

    this._synchScreens(data);
  }

  unselectScreen(screen: BehaviorScreen, multiple: boolean = false) {
    const data = {
      Monitor: screen,
      IsSelected: false,
      Multiselect: multiple
    };

    this._synchScreens(data);
  }

  changeScreens(screens: BehaviorScreen[]) {
    if (screens && screens.constructor === Array) {
      if (
        screens.length !== this._selectedScreens.length ||
        !_.isEqual(screens, this._selectedScreens)
      ) {
        this.connectScreens(screens);
        this._selectedScreens = [...(screens ? screens : [])];
        this.selectedScreensChanged.next([...(screens ? screens : [])]);
      }
    }
  }

  updateMonitorVisibility(locationFilter, nameFilter = ''): void {
    this._screens.forEach((screen: BehaviorScreen) => {
      screen.isVisible =
        (locationFilter.PhysicalAddress === null ||
          screen.MachinePhysicalAddress === locationFilter.PhysicalAddress) &&
        (nameFilter === null ||
          screen.MonitorTitle.toLowerCase().includes(nameFilter.toLowerCase()));
    });
  }

  updateAssignments(itemlist: PlaylistDTO, isToStartPlaylist: boolean) {
    const assignments: PluralAssignment = new PluralAssignment([], itemlist.Id);

    this._selectedScreens.forEach(screen => {
      if (
        !(
          itemlist.MachinePhysicalAddress != null &&
          screen.MachinePhysicalAddress !== itemlist.MachinePhysicalAddress ||
          screen.Machine.IsAndroid !== itemlist.IsAndroid
        )
      ) {
        assignments.MonitorKeys.push(
          new MonitorKey(screen.MonitorHandler, screen.MachinePhysicalAddress)
        );

        if (screen.Machine.IsAndroid) {
          screen.Status = ScreenContentStatus.Pending;
        }
      }
      else {
        this._selectedScreens = this._selectedScreens.filter(selectedScreen =>
          selectedScreen.MonitorHandler !== screen.MonitorHandler);

        this.selectedScreensChanged.next(this._selectedScreens);
      }
    });

    if (assignments.MonitorKeys.length === 0) {
      return;
    }

    this.subscriptions.add(
      this.monitorsDataService
        .assignPlaylistToPlural(assignments)
        .subscribe(() => {
          const assignedScreens = this._selectedScreens.filter(screen =>
            assignments.MonitorKeys.some(monitorKey =>
              monitorKey.MonitorHandler === screen.MonitorHandler && monitorKey.MachinePhysicalAddress === screen.MachinePhysicalAddress));

          const monitorsList = _.map(assignedScreens, (screen: BehaviorScreen) => {
            return `'${screen.MonitorTitle}'`;
          }).join(', ');

          if (!itemlist.IsAndroid) {
            this.toastrService.success(
              `${PlaylistDialogMessagesEnum.Playlisttitle} '${itemlist.PlaylistTitle
              }' ${ToastrMessageEnum.SuccessfullyAssignedToTitle} ${monitorsList}`,
              ToastrMessageEnum.AssignmentSuccessfulTitle
            );
          }

          this.playlistSelectionChanged.next(itemlist);

          if (isToStartPlaylist) {
            this._selectedScreens.forEach(
              screen => {
                screen.Status = ScreenContentStatus.Waiting;
                screen.playPlaylist(itemlist, screen.Machine.IsAndroid);
              }
            );
          }
        },
        () => this.playlistSelectionChanged.next(this.playlistSelectionChanged.value))
    );
  }

  updateTagAssignments(itemlist: TagViewModel): Observable<void> {
    const assignments: MonitorTagPluralAssignmentModel = new MonitorTagPluralAssignmentModel([], itemlist.Id);

    this._selectedScreens.forEach(screen => {
      if (
        !(
          itemlist.MachinePhysicalAddress != null &&
          screen.MachinePhysicalAddress !== itemlist.MachinePhysicalAddress
        )
      ) {
        assignments.MonitorKeys.push({
          MachinePhysicalAddress: screen.MachinePhysicalAddress,
          MonitorHandler: screen.MonitorHandler
        });
      }
    });

    if (assignments.MonitorKeys.length === 0) {
      return of();
    }

    return this.tagService.assignTagToPlural(assignments).pipe(
      tap(() => {
        const monitorsList = _.map(this._selectedScreens, o => {
          return `'${o.MonitorTitle}'`;
        }).join(', ');
        this.toastrService.success(
          `${MonitorTagDialogMessagesEnum.TagTitle} '${itemlist.Title
          }' ${ToastrMessageEnum.SuccessfullyAssignedToTitle} ${monitorsList}`,
          ToastrMessageEnum.AssignmentSuccessfulTitle
        );
      })
    );
  }

  dismissAsignmentAndStopPlaylist(playlistId: number): Observable<void[]> {
    return this.screensCollectionChanged.pipe(
      map(screens => {
        const observables: Observable<void>[] = [];

        const assignedScreens = screens.filter(screen =>
          screen.AssignedPlaylist?.Id === playlistId);

        const screensWithRunningPlaylist = assignedScreens.filter(screen =>
          screen.Status === ScreenContentStatus.Playing);

        if (assignedScreens.length) {
          const monitorKeys = assignedScreens.map(screen =>
            new MonitorKey(screen.MonitorHandler, screen.MachinePhysicalAddress));
          observables.push(this.monitorsDataService.dismissAssignment(monitorKeys));
        }

        if (screensWithRunningPlaylist.length) {
          const monitorKeys = screensWithRunningPlaylist.map(screen =>
            new MonitorKey(screen.MonitorHandler, screen.MachinePhysicalAddress));
          observables.push(this.playerService.stopPlaylistOnAllMonitors(monitorKeys));
        }

        return observables;
      }),
      filter(observables => observables.length > 0),
      mergeMap(observables => forkJoin(observables))
    );
  }

  _reloadLocations(updatedLocations: Machine[] = []) {
    const locations = this.locationsChanged.value;

    // remove old
    const locationsToRemove = _.filter(
      locations,
      (loc: Machine) =>
        !_.some(
          this._screens,
          (scr: BehaviorScreen) =>
            scr.MachinePhysicalAddress === loc.PhysicalAddress
        )
    );

    locationsToRemove.forEach(locRem => {
      const index = locations.findIndex(x => locRem === x);
      if (index >= 0) {
        locations.splice(index, 1);
      }
    });

    // add & ensure actual
    this._screens.forEach(scr => {
      const locs = _.filter(
        locations,
        loc => loc.PhysicalAddress === scr.MachinePhysicalAddress
      );
      if (locs.length) {
        scr.Machine = locs[0];
      } else {
        locations.push(scr.Machine);
      }
    });

    // update needed
    updatedLocations.forEach(updLoc => {
      const index = locations.findIndex(
        x => updLoc.PhysicalAddress === x.PhysicalAddress
      );
      if (index >= 0) {
        locations[index].Title = updLoc.Title;
        locations[index].UserId = updLoc.UserId;
        locations[index].Version = updLoc.Version;
        locations[index].CategoryId = updLoc.CategoryId;
      }
    });

    // notify subscribers
    this.locationsChanged.next(locations);
    this.isLocationLoading.next(false);
  }

  setShowPrimaryAll(value: boolean) {
    this.locationsChanged.value.forEach(l => (l.ShowPrimaryScreen = value));
  }

  _reloadActiveMonitors() {
    this.subscriptions.add(
      this.monitorsDataService
        .getMonitors()
        .subscribe((monitors: Monitor[]) => {
          if (!this.monitorHub.connection) {
            this.monitorHub.connectAsUser();
          }

          this.addMonitors(monitors);
        })
    );
  }

  _synchScreens(info: IMonitorSelectInfo) {
    if (!info) {
      return;
    }

    this.ngZone.run(() => {
      const collection = info.Multiselect ? this._selectedScreens : [];
      if (info.IsSelected) {
        this._selectedScreens = _.union(collection, [info.Monitor]);
        this.selectedScreensChanged.next([...(this._selectedScreens ? this._selectedScreens : [])]);
        this.uiService.showBottomPanel();
      } else {
        if (_.includes(collection, info.Monitor)) {
          this._selectedScreens = _.without(collection, info.Monitor);
          this.selectedScreensChanged.next([...(this._selectedScreens ? this._selectedScreens : [])]);
        }

        if (this._selectedScreens.length === 1 && _.isEqual(this._selectedScreens[0], info.Monitor)) {
          this.selectedScreensChanged.next([]);
        }
      }
    });
  }

  private initializeVariables() {
    this.subscriptions = new Subscription();
    this._screens = [];
    this.activeMonitorTagsChanged.next([]);
    this.monitorTagChanged.next(null);
    this.selectedScreensChanged.next([]);
    this.playlistStateDataChanged.next(new Map());
    this.uiService.bottomPanelVisibleSubject.next(false);
    this.uiService.bottomPanelPinnedSubject.next(false);
    this.uiService.libraryEditItem$.next(null);
    this.uiService.isSchedulePanelExpanded = false;
    this.uiService.isRightPlaylistPanelExpanded = false;
    this.uiService.isTagsPanelExpanded = false;
    this.playlistDataService.playlistCollectionChanged.next([]);
  }

  public createBehaviorScreen(monitor: Monitor): BehaviorScreen {
    const monitorHub = new MonitorHub(this.config, this.route, this.toastrService, this.authService);

    return new BehaviorScreen(
      monitor,
      monitorHub,
      this.ngZone,
      this.monitorsDataService,
      this.playerService,
      this.toastrService,
      this.monitorChangeViewService,
      this.playlistDataService,
      this
    );
  }

  public setPendingState(screens: BehaviorScreen[]): void {
    const pendingScreens = this.pendingScreens.value || new Map();

    screens.forEach(screen => {
      const monitorKey = screen.MonitorHandler + screen.MachinePhysicalAddress;

      screen.Status = ScreenContentStatus.Pending;
      pendingScreens.set(monitorKey, screen.ScreenState.ScreenContentStatus);
      this.pendingScreens.next(pendingScreens);
    });
  }

  public dropItem(event: CdkDragDrop<string[]>, monitor: BehaviorScreen, isSelected: boolean): void {
    const droppedItem: PlaylistDTO | TagViewModel = event?.item?.data?.data;
    const isDropToTheWrongLocation: boolean = droppedItem?.MachinePhysicalAddress &&
      droppedItem.MachinePhysicalAddress !== monitor.MachinePhysicalAddress;

    if (!this.instanceOfTagViewModel(droppedItem) && (droppedItem.IsAndroid !== monitor.Machine.IsAndroid)) {
      droppedItem.IsAndroid ?
        this.toastrService.error(MonitorMenuDialogDessagesEnum.AssignmentAndroidPlaylistToWindowsLocationUnsuccessfulDescription,
          ToastrMessageEnum.AssignmentUnsuccessfulTitle) :
        this.toastrService.error(MonitorMenuDialogDessagesEnum.AssignmentWindowsPlaylistToAndroidLocationUnsuccessfulDescription,
          ToastrMessageEnum.AssignmentUnsuccessfulTitle);

      return;
    }

    if (isDropToTheWrongLocation) {
      this.toastrService.error(MonitorMenuDialogDessagesEnum.AssignmentUnsuccessfulDescription,
        ToastrMessageEnum.AssignmentUnsuccessfulTitle);
      return;
    }

    if (!event.isPointerOverContainer) {
      return;
    }

    if (!isSelected) {
      this.unselectAll();
      this.selectScreen(monitor);
    }
    this.uiService.handleItemlistDrop(event.item.data, monitor);
  }

  public disconnectScreens(screens: BehaviorScreen[]) {
    const connectedScreens = screens.filter(screen => screen.isConnected && screen instanceof BehaviorScreen);
    this.requestImageInSnapshot(connectedScreens, false);

    connectedScreens.forEach(screen => {
      screen.disconnect();
    });
  }

  public connectScreens(screens: BehaviorScreen[]) {
    const disconnectedScreens = screens.filter(screen => !screen.isConnected && screen instanceof BehaviorScreen);

    disconnectedScreens.forEach(screen => {
      screen.connect();
    });

    this.requestImageInSnapshot(disconnectedScreens, true);
  }

  public connectFullScreenMonitorAndDisconnectRest(screens: BehaviorScreen[], fullScreenMonitor: BehaviorScreen) {
    this.connectScreens([fullScreenMonitor]);

    const monitorsExceptFullScreen = this.getRestMonitorsExceptFullScreen(screens, fullScreenMonitor);
    const screensToDisconnect = this.filterOutSelectedScreens(monitorsExceptFullScreen);

    this.disconnectScreens(screensToDisconnect);
  }

  public filterOutSelectedScreens(screens: BehaviorScreen[]): BehaviorScreen[] {
    return screens.filter(screen =>
      !this._selectedScreens.find(selectedScreen =>
        selectedScreen.MachinePhysicalAddress === screen.MachinePhysicalAddress &&
        selectedScreen.MonitorHandler === screen.MonitorHandler));
  }

  public filterNewLocations(incomingLocations: Machine[], currentLocations: Machine[]): Machine[] {
    return incomingLocations.filter(({ PhysicalAddress }) =>
      !currentLocations.some(({ PhysicalAddress: existingPhysicalAddress }) =>
        existingPhysicalAddress === PhysicalAddress
    ));
  }

  private requestImageInSnapshot(screens: BehaviorScreen[], isToShowImage: boolean) {
    const androidScreens = screens.filter(screen => screen.Machine.IsAndroid);

    if (androidScreens.length) {
      const physicalAddresses = screens.map(screen => screen.MachinePhysicalAddress);

      this.machinesService.showImageInSnapshot(physicalAddresses, isToShowImage)
        .pipe(take(1))
        .subscribe();
    }
  }

  public getRestMonitorsExceptFullScreen(monitors: BehaviorScreen[], fullScreenMonitor: BehaviorScreen) {
    return monitors.filter(monitor => monitor &&
      !(monitor.MachinePhysicalAddress === fullScreenMonitor.MachinePhysicalAddress &&
        monitor.MonitorHandler === fullScreenMonitor.MonitorHandler));
  }

  public getScreensState(): ScreensState {
    const selectedScreens = this.selectedScreensChanged.value;

    if (!selectedScreens?.length) {
      return new ScreensState();
    }

    if (!this.differ || this.lastSelectedScreens !== selectedScreens) {
      this.differ = this.differs.find(selectedScreens).create((index, item) => item?.ScreenState?.ScreenContentStatus);
      this.lastSelectedScreens = selectedScreens;
      this.cachedScreensState = this.checkScreensStatus(selectedScreens);
      return this.cachedScreensState;
    }

    const changes = this.differ.diff(selectedScreens);

    if (changes) {
      this.cachedScreensState = this.checkScreensStatus(selectedScreens);
    }

    return this.cachedScreensState;
  }

  public filterSnapshots(imageSrc: string): Observable<ImageBitmap> {
    return this.monitorChangeViewService.getSnapshot(imageSrc).pipe(
      switchMap(blob =>
        from(createImageBitmap(blob)).pipe(
          filter(img =>
            img.height > AppConstants.DEFAULT_ANDROID_DEVICE_IMAGE_HEIGHT ||
            img.width > AppConstants.DEFAULT_ANDROID_DEVICE_IMAGE_WIDTH
          )
        )
      )
    );
  }

  public addMonitors(monitors: Monitor[]) {
    monitors.forEach(data => {
      const index = this._screens.findIndex(screen =>
        screen.MachinePhysicalAddress === data.MachinePhysicalAddress &&
        screen.MonitorHandler === data.MonitorHandler
      );

      if (index < 0) {
        const screen = this.createBehaviorScreen(data);

        screen.SchedulesUpdated.subscribe(() =>
          this.someSchedulesUpdated.next()
        );
        screen.ProfileUpdated.subscribe(data =>
          this.someScreenUpdated.next(data)
        );
        this._screens.push(screen);
      }
      else {
        this._screens[index].DesktopVersion = data.DesktopVersion;
      }
    });

    this.updateScreensState();
  }

  private removeInactiveMonitors(data: MonitorKey) {
    this._screens = this._screens.filter(screen => {
      const screenToRemove = screen.MachinePhysicalAddress === data.MachinePhysicalAddress &&
        screen.MonitorHandler === data.MonitorHandler;

      if (screenToRemove) {
        screen.destroy();
      }
      return !screenToRemove;
    });

    this._selectedScreens = this._selectedScreens.filter(screen =>
      !(screen.MachinePhysicalAddress === data.MachinePhysicalAddress && screen.MonitorHandler === data.MonitorHandler));

    this.updateScreensState();
  }

  private updateScreensState() {
    this._reloadLocations();
    this.screensCollectionChanged.next(this._screens);
  }

  private instanceOfTagViewModel(object: PlaylistDTO | TagViewModel): object is TagViewModel {
    return AppConstants.TAG_COLOR_PROPERTY in object;
  }

  private checkScreensStatus(screens: BehaviorScreen[]): ScreensState {
    const screensState = new ScreensState();
    screensState.isAllPlaying = true;
    screensState.isAllWaiting = true;

    for (const screen of screens) {
      const state = screen.ScreenState?.ScreenContentStatus;

      screensState.isAnyPending ||= state === ScreenContentStatus.Pending;
      screensState.isAnyUploading ||= state === ScreenContentStatus.Uploading;
      screensState.isAllPlaying &&= state === ScreenContentStatus.Playing;
      screensState.isAllWaiting &&= state === ScreenContentStatus.Waiting;
      screensState.isBusy ||= [ScreenContentStatus.Uploading, ScreenContentStatus.Pending].includes(state);
    }

    return screensState;
  }

  destroy() {
    this.screensCollectionChanged.next(null);
    this._screens.forEach(x => x.destroy());
    this.subscriptions.unsubscribe();
    this.monitorHub.disconnect();
  }
}
